import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { useIpQueryContext } from "context/IpQueryContext";
import { useIntl } from "react-intl";
import IpCheckerTable from "../IpCheckerTable";
// import IpCheckerMap from "../IpCheckerMap";
import "../IpCheckerTabs/IpCheckerTabs.css";
import {
  Center,
  Heading,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { useIntlContext } from "context/TranslationContext";
import NoSubscriptionComponent from "../NoSubscriptionComponent";
import { RequestMethod, RequestName } from "hooks/api/apiHookTypes";
import useApiRequest from "hooks/api/useApiHook";
import ErrorText from "components/API/ErrorText";
import LoaderSpinner from "components/UIComponents/LoaderSpinner";
import { getCountryCodeForFlag } from "utils/countryMapping";
import PortCheckBlock from "../PortCheckBlock";
import { usePortCheckAvailability } from "hooks/api/use-port-check-availability";
import CheckIpErrorMessage from "../CheckIpErrorMessage";

const IpCheckerFreeData = () => {
  const defaultApiKey = useSelector((state: RootState) => state.apiKey.apiKey);
  const userIp = useSelector((state: RootState) => state.userIp.userIp);
  const [tabsList, setTabslist] = useState<any>([]);
  const [country, setCountry] = useState("");

  const intl = useIntl();

  const { queryIp } = useIpQueryContext();
  // Hidden until the api confirms the feature is live (fails closed).
  const portCheckAvailable = usePortCheckAvailability();
  const { locale } = useIntlContext();

  // console.log("🔍 [FREE_DATA] Rendering free version with queryIp:", queryIp);

  useEffect(() => {
    setTabslist([]);
    setCountry(null);
  }, [queryIp]);

  const { data, loading, error } = useApiRequest({
    method: RequestMethod.GET,
    name: RequestName.CHECK_IP,
    apiToken: defaultApiKey,
    checkIpParams: {
      queryIp: queryIp,
    },
  });

  useEffect(() => {
    // A failed lookup must not leave the previous IP's result on screen:
    // `data` survives a failed re-fetch in useApiRequest.
    if (error) {
      setTabslist([]);
      setCountry(null);
      return;
    }
    if (data && !loading) {
      const freeIpData = [
        {
          title: intl.formatMessage({ id: "GeneralInfo" }),
          data: [{ ...data }],
        },
        {
          title: "IpIntel",
        },
        {
          title: "IpData",
        },
        {
          title: "Whois",
        },
        {
          title: "IpAPI",
        },
        {
          title: "+5 Other providers",
        },
      ];
      setCountry(data.country);
      setTabslist(freeIpData);
    }
  }, [data, locale, intl, loading, error]);

  return (
    <main className="ip-checker-tabs">
      <Center width="100%">
        <div className="ip-heading">
          {country && (
            <img
              width={50}
              height={40}
              src={`/flags/${getCountryCodeForFlag(country)}.svg`}
              alt={country}
            />
          )}
          <Heading>{queryIp}</Heading>
        </div>
      </Center>
      {queryIp && tabsList.length ? (
        <Center className="ip-checker-tabs-container">
          <Tabs>
            <>
              <TabList>
                {tabsList?.map((tab, index) => (
                  <Tab className="checker-block-tab-button" key={index}>
                    {tab.title}
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                {tabsList?.map((tab, index) => (
                  <TabPanel key={index}>
                    {tabsList.indexOf(tab) === 0 ? (
                      <IpCheckerTable
                        title={tab.title}
                        data={tab.data}
                        type="free"
                      />
                    ) : (
                      <NoSubscriptionComponent />
                    )}
                  </TabPanel>
                ))}
              </TabPanels>
            </>
          </Tabs>
        </Center>
      ) : loading ? (
        <Center>
          <LoaderSpinner />
        </Center>
      ) : error ? (
        <CheckIpErrorMessage error={error} />
      ) : (
        !tabsList.length && data && <ErrorText />
      )}
      {/* Free path: own IP only, and anonymous. The block sends no IP (the
          server checks the IP it detected for this request, never a typed
          one) and no Authorization header — the label shows no cost, so the
          call must not be able to spend a request even if this user has
          quota we failed to read. Hidden for anyone else's IP. */}
      {portCheckAvailable &&
        userIp &&
        queryIp === userIp &&
        tabsList.length > 0 &&
        !loading && (
        <PortCheckBlock key={queryIp} />
      )}
    </main>
  );
};

export default IpCheckerFreeData;
